

import { type GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { cloneDeep} from 'lodash';
import React, { ReactNode, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CustomStatus,
  CustomTooltipWithLabel,
  DashboardLoader,
  FilterLayout,
  FilterProp,
  renderHeaderActionButtons,
  SidePopover,
  TableDashboard,
  useAppDispatch,
  useAppSelector,
} from '../../globalUtils/globalExports';


import {
  getUsbCertificatesReducer,
  getGlobalReducer,
  setTableColumnPrefrences,
  fetchChargerCertificatesInfoData,
  setChargerCertificatesInfoListTableProps,
  usbCertificatesType,
  getCurrentScreenAppliedFiltersCount,
  setFilterData,
  getFilterSelectedValue,
  getFilterReducer,
  getReferenceDataReducer,
  getCurrentFiltersValues,
  fetchChargerVisibleIdsPaginatedList,
  pushChargerCertificate,
} from '../../rmsReduxStore/reduxExports';
import './chargerCertificatesInfo.css';
import { NoDataComponentUsbCertificates } from '../../globalUtils/TableDashboard/TableComponents';
import { downloadFile, formatRelativeTimeForTable } from '../../globalUtils/globalHooks';
import access from '../../auth/service/AccessControl';


import { IconWithTooltip } from '../../globalUtils/globalIcons';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';





const ChargerCertificatesInfo = memo(function ChargerCertificatesInfo() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const layouts = ['list', 'map'];
  const { chargerCertificatesInfo, loader, chargerCertificatesInfoListTableProps,chargerCertificatesInfoCount } = useAppSelector(getUsbCertificatesReducer);
  const chargerCertificatesInfoListTablePropsRef = useRef(chargerCertificatesInfoListTableProps);
  const chargerCertificatesInfoRef = useRef(chargerCertificatesInfo);
  const {screenTableColumnPrefrences} = useAppSelector(getGlobalReducer)
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [isPushingCertificate, setIsPushingCertificate] = useState(false);
  const { screenFilters } = useAppSelector(getFilterReducer)
  const {
    chargingStationReferenceData,
    chargerProductTypeReferenceData,
    chargerVisibleIdReferenceData,
    certificateStatusReferenceData,
    userReferenceData
  } = useAppSelector(getReferenceDataReducer);

  // Warn before reload / tab close while a certificate is being pushed
  useEffect(() => {
    if (!isPushingCertificate) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return (): void => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isPushingCertificate]);

  useEffect(() => {
    chargerCertificatesInfoListTablePropsRef.current = chargerCertificatesInfoListTableProps;
  }, [chargerCertificatesInfoListTableProps]);

  useEffect(() => {
    chargerCertificatesInfoRef.current = chargerCertificatesInfo;
  }, [chargerCertificatesInfo]);

  const onChangeOfSearchText = useCallback(async (searchTerm) => {
    if (chargerCertificatesInfoListTablePropsRef?.current) {
      await layoutCallback(1, chargerCertificatesInfoListTablePropsRef?.current?.pageSize, 'list', chargerCertificatesInfoListTablePropsRef?.current?.sortBy, chargerCertificatesInfoListTablePropsRef?.current?.order, null, searchTerm)
    }
  }, [chargerCertificatesInfo, chargerCertificatesInfoListTablePropsRef?.current, chargerCertificatesInfoRef?.current]);

  const searchBoxConfiguration = useMemo(() => {
    return {
      searchFieldId: 'manage-charger-parameters-search-box',
      searchFieldName: 'manage-charger-parameters-search-box',
      handleSearch: onChangeOfSearchText,
    };
  }, [onChangeOfSearchText]);

  // TODO: Status Icon display
  const getStatusIconClass = {
    'InProgress': 'status__in-progress__parameter__icon',
    'Scheduled': 'status__scheduled__parameter__icon',
    'Completed': 'status__completed__parameter__icon',
    'Enabled':'status__completed__parameter__icon',
  }



  const headerActionBtns = renderHeaderActionButtons({
    actionBtns:
      [
        {
          id: 'add_new_certificate',
          isAccessible: access?.chargers?.add() || access?.chargers?.edit(),
          headerActionBtnClick: (): void => {
            navigate('/add-charger-certificate');
          }
        },
        {
          id: 'add_filter_btn',
          isAccessible: access?.chargers?.view(),
          headerActionBtnClick: (): void => {
            setFiltersVisible(true);
          },
          badgeCount: getCurrentScreenAppliedFiltersCount('chargerCertificates')
        },
        // {

        //   id: 'export_csv_btn',
        //   isAccessible: access?.chargers?.export(),
        //   headerActionBtnClick: (event): void => {
        //     setExportAnchorEl(event.currentTarget);

        //   }
        // },
      ]
  })

  // const handleClosePopOver = (): void => {
  //   setIsSidePopOverOpen(false)
  // }
  const handleFiltersClosePopOver = (): void => {
    setFiltersVisible(false);
  }
  // TODO: Table Layout Callbacks
  const mobileViewConfiguration = useMemo(() => {
    return {
      headerDataConfig: {
        headerLeftDataConfig: ['uniqueId','visibleId','macId','ocppId','chargingStationName','customerName','productType','certificateStatus'],
        headerRightDataConfig: {
          actionIconsComponent: true,
          statusIconComponent: true,
        },
      },
      statusIconKey: '',
      statusIconMapper: getStatusIconClass,
      contentDataConfig: [
        [
          {
            key: 'expiryDate',
            iconClassName: 'parameters_createdOn_icon',
          },
        ],
      ],
    };
  }, []);

  const getTableRows = useCallback((): IChargerCertificatesInfo[] => {
    return chargerCertificatesInfo !== null && chargerCertificatesInfo?.length > 0 ? chargerCertificatesInfo : [];
  }, [chargerCertificatesInfo]);


  
  const onDownloadFileIconClick = (rowData) => {
    if(!rowData?.fileURL) {
      return toast.warn('No certificates present.');
    }
    try{
      downloadFile({ url: rowData?.fileURL });
      return toast.success('File downloaded.');
    } catch{
      return toast.error('Download failed.');
    }

}

  const onPushCertificateClick = async (rowData) => {
    if (!rowData?.id || isPushingCertificate) return;
    setIsPushingCertificate(true);
    try {
      const { data, status } = await dispatch(pushChargerCertificate({ certId: rowData.id }));
      if (status === 200) {
        toast.success(data?.message ?? 'Certificate successfully pushed to the charger.');
      } else {
        toast.error(data?.message ?? 'Failed to push certificate to the charger.');
      }
    } finally {
      setIsPushingCertificate(false);
    }
  }

const onResetButtonClick = useCallback(async (callListApi: boolean) => {
  const res = await setFilterData(null, '', 'chargerCertificates', 'CLEAR', false)(dispatch);
  if (res?.message === 'Action dispatched successfully') {
    if (chargerCertificatesInfoListTablePropsRef?.current && callListApi) {
      layoutCallback(1, chargerCertificatesInfoListTablePropsRef?.current?.pageSize, chargerCertificatesInfoListTablePropsRef?.current?.view, chargerCertificatesInfoListTablePropsRef?.current?.sortBy, chargerCertificatesInfoListTablePropsRef?.current?.order, res?.filters, chargerCertificatesInfoListTablePropsRef?.current?.searchTerm)
    }
  }
}, [chargerCertificatesInfoListTablePropsRef?.current])

const filterFooterBtns = useMemo(() => {
  return [
    {
      buttonText: 'Reset',
      buttonId: 'Reset',
      btnClassName: 'secondary__btn',
      handleClick: (): Promise<void> => onResetButtonClick(true),
      isDisabled: false,
      buttonVariant: 'outlined',
    },
  ];
}, [onResetButtonClick]);

  const actionIconsList = useCallback(()=>{
    return [
    {
        icon: 'rms__documentation__download__icon',
        tooltipText: 'Download Certificate',
        fieldId:'rms-support-documentation-download-btn',
        className:'rms-support-documentation-download__icon__wrap',
        isAccessible: true,
        action: onDownloadFileIconClick
    }
  ]
},[])
  const chargerCertificatesInfoColumns = useMemo(() => {

    return [
      {
        field: 'visibleId',
        headerName: 'Charger ID',
        flex: .6,
        sortable: true,
        minWidth: 150,
      },
      {
        field: 'macId',
        headerName: 'MAC ID',
        flex: .6,
        sortable: true,
        minWidth: 150,
      },
      {
        field: 'ocppId',
        headerName: 'OCPP ID',
        flex: .6,
        sortable: true,
        minWidth: 120,
        renderCell: (params) => (params?.row?.ocppId ?? '-')
      },
      {
        field: 'chargingStationName',
        headerName: 'Charging Station',
        flex: .6,
        sortable: true,
        minWidth: 120,
        renderCell: (params) => (<CustomTooltipWithLabel label={params?.row?.chargingStationName ?? '-'} labelId={`charger-charging-station-${params?.row?.id}`} />)
      },
      {
        field: 'customerName',
        headerName: 'Customer',
        flex: .6,
        sortable: true,
        minWidth: 130,
        renderCell: (params) => (<CustomTooltipWithLabel label={params?.row?.customerName ?? '-'} labelId={`charger-customer-${params?.row?.id}`} />)
      },
      {
        field: 'chargerStatus',
        headerName: 'Charger Status',
        flex: .6,
        sortable: true,
        minWidth: 130,
        renderCell: (params: GridRenderCellParams<GridValidRowModel, Date>): JSX.Element => {
          const status = params?.row?.chargerStatus;
          return status != null ? 
            <CustomStatus
              statusText={status}
              statusIconClassName={status === 'Online' ? 'status__online__charger__icon' : 'status__offline__charger__icon'}
              backgroundColor={status === 'Online' ? 'var(--green-color)' : 'var(--grey-color)'} /> 
            : 
            <CustomTooltipWithLabel label={'NA'} labelId={`charger-status-${params?.row?.id}`} />;
        },
      },
      {
        field: 'productType',
        headerName: 'Product Type',
        flex: .6,
        sortable: true,
        minWidth: 120,
      },
      {
        field: 'certificateType',
        headerName: 'Certificate Type',
        flex: .6,
        sortable: true,
        minWidth: 120,
      },
      {
        field: 'certificateStatus',
        headerName: 'Certificate Status',
        flex: .6,
        sortable: true,
        minWidth: 120,
        renderCell: (params) => {
          const status = params?.row?.certificateStatus;
        
          let backgroundColor = '';
          let color = '';
          let borderColor = '';
        
          switch (status) {
            case 'Inactive':
              color = '#5F6B7A';
              backgroundColor = '#F1F4F7';
              borderColor = '#C7D0D9';
              break;
            
            case 'Active':
              color = '#2E7D32';
              backgroundColor = '#E6F4EA';
              borderColor = '#81C784';
              break;
        
            case 'Expired':
              color = '#C62828';
              backgroundColor = '#FDEAEA';
              borderColor = '#C62828';
              break;
          }
        
          return (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <span
                style={{
                  color,
                  backgroundColor,
                  border: `2px solid ${borderColor}`,
                  fontSize: '12px',
                  fontWeight: 500,
                  borderRadius: '5px',
                  padding: '4px 12px',
                  minWidth: '80px',
                  textAlign: 'center'
                }}
              >
                {status}
              </span>
            </div>
          );
        }
      },
      {
        field: 'expiryDate',
        flex: .6,
        headerName: 'Expiry Date',
        minWidth: 180,
        renderCell: (
          params: GridRenderCellParams<GridValidRowModel>
        ): ReactNode => {
          const rowData = params?.row;
          const date = new Date(rowData?.expiryDate);
          const formattedDate = date.toLocaleString('en-US', {
            month: 'long',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });
          return formattedDate;
        },
      },
      {
        field: 'createdBy',
        flex: .6,
        headerName: 'Generated By',
        minWidth: 150,
      },
      {
        field: 'createdAt',
        flex: .6,
        headerName: 'Generated On',
        minWidth: 180,
        renderCell: (
          params: GridRenderCellParams<GridValidRowModel>
        ): ReactNode => {
          const rowData = params?.row;
          const date = new Date(rowData?.createdAt);
          return formatRelativeTimeForTable(date);
        },
      },
      {
        field: 'action',
        headerName: 'Actions',
        flex: .6,
        minWidth: 240,
        sortable: false,
        pinnable: false,
        renderCell: (params) => {
          const rowData = params?.row;
          return (
            <div className='rms__support__documentation__action__icon__wrap charger__certificate__actions'>
              {
                actionIconsList().map(actionBtn=>{
                  if(!actionBtn?.isAccessible)return;
                  return (
                    <div key={actionBtn.fieldId} className={actionBtn.className} onClick={()=>actionBtn.action(rowData)}>
                      <IconWithTooltip  iconClassName={actionBtn?.icon} toolTipText={actionBtn?.tooltipText}/>
                    </div>
                  )
                })
              }
              {rowData?.certificateStatus === 'Inactive' && (access?.chargers?.add() || access?.chargers?.edit()) && (
                <>
                  <div
                    className={`charger__certificate__push__icon__wrap ${!rowData?.isOnRms || rowData?.isSpurious ? 'charger__certificate__push__icon__wrap--disabled' : ''}`}
                    onClick={() => rowData?.isOnRms && !rowData?.isSpurious && onPushCertificateClick(rowData)}
                  >
                    <IconWithTooltip iconClassName='panel_ftp_log_upload_icon' toolTipText='Push Certificate' />
                  </div>
                  {rowData?.isSpurious ? (
                    <>
                      <span className='charger__certificate__spurious__tag'>Spurious Charger</span>
                      <IconWithTooltip iconClassName='rms_info_icon-black' toolTipText='Certificate upload is not supported for spurious chargers. Please update the Charger ID, generate a new charger-specific certificate, and try again.' />
                    </>
                  ) : !rowData?.isOnRms && (
                    <IconWithTooltip iconClassName='rms_info_icon-black' toolTipText='Charger is not available on RMS.' />
                  )}
                </>
              )}
            </div>
          )
        }
      }
    ];
  }, [isPushingCertificate]);

  const handleFilterDataChange = useCallback(async (val, filterKey, isGlobal) => {
    const res = await setFilterData(val, filterKey, 'chargerCertificates', 'SET', isGlobal ? isGlobal : false)(dispatch)
    if (res?.message === 'Action dispatched successfully') {
      if (chargerCertificatesInfoListTablePropsRef?.current) {
        await layoutCallback(1, chargerCertificatesInfoListTablePropsRef?.current?.pageSize, chargerCertificatesInfoListTablePropsRef?.current?.view, chargerCertificatesInfoListTablePropsRef?.current?.sortBy, chargerCertificatesInfoListTablePropsRef?.current?.order, res?.filters, chargerCertificatesInfoListTablePropsRef?.current?.searchTerm)
      }
    }
  }, [chargerCertificatesInfoListTablePropsRef?.current, dispatch])



  const layoutCallback = useCallback(async (pageNumber: number, pageSize: number, view: string, sortField: string, sortOrder: string, filterData, searchTerm) => {
    const tableProps: IChargerListTableProps = cloneDeep(chargerCertificatesInfoListTablePropsRef?.current)
    const filterValues = filterData ? filterData : getCurrentFiltersValues('chargerCertificates')
    await dispatch(fetchChargerCertificatesInfoData({
      sortBy: sortField || chargerCertificatesInfoListTablePropsRef?.current?.sortBy,
      order: sortOrder || chargerCertificatesInfoListTablePropsRef?.current?.order,
      pageSize: pageSize || 15,
      pageNumber,
      filters: filterValues,
      searchTerm: searchTerm != null && searchTerm != undefined ? searchTerm : chargerCertificatesInfoListTablePropsRef?.current?.searchTerm
    }));

    if (tableProps) {
      tableProps.view = view ?? 'list'
      tableProps.sortBy = sortField || chargerCertificatesInfoListTablePropsRef?.current?.sortBy;
      tableProps.order = sortOrder || chargerCertificatesInfoListTablePropsRef?.current?.order;
      tableProps.pageNumber = pageNumber;
      tableProps.pageSize = pageSize || 15;
      if (searchTerm != null && searchTerm != undefined) {
        tableProps.searchTerm = searchTerm;
      }
    }
    await dispatch(setChargerCertificatesInfoListTableProps(tableProps))
  }, [chargerCertificatesInfoListTablePropsRef?.current, dispatch]);

    // Handler For Column Prefrence Chnages
    const handleColumnPrefrenceChange = useCallback(async (operation,data) => {
      await dispatch(setTableColumnPrefrences('chargerCertificatesInfo',operation,data))
   },[])

  // Redux state Clean up
  const chargerCertificatesInfoCleanUpState = useCallback(() => {
    const action = {
      type: usbCertificatesType.CLEAR_CHARGER_CERTIFICATES_INFO_LIST_DATA
    }
    dispatch(action);
  }, [])
  useEffect(() => {
    window.addEventListener('beforeunload', chargerCertificatesInfoCleanUpState);
    return (): void => {
      chargerCertificatesInfoCleanUpState()
      window.removeEventListener('beforeunload', chargerCertificatesInfoCleanUpState);
    }
  }, [])

  const chargerFilterProps: FilterProp[] = useMemo(() => {
    const filterCertificateExpiryTimeStampValue = getFilterSelectedValue('certificateExpiryIntervalRange', 'chargerCertificates', false);
    const filterCertificateCreatedAtTimeStampValue = getFilterSelectedValue('certificateCreatedAtInterval', 'chargerCertificates', false);

    const chargerFilters: FilterProp[] =
      [
        {
          filterLabel: 'Charging Stations',
          filterType: 'dropdown',
          filterId: 'chargingStationId',
          filterDropdownProps: {
            selectValue: getFilterSelectedValue('chargingStationId', 'chargerCertificates', false),
            selectOptions: chargingStationReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'chargingStationId', false)
            },
            selectDropDownId: 'charger-chargingStationId-type-filter-dropdown',
            isMultiSelect: true
          }
        },
        {
          filterLabel: 'Product Type',
          filterType: 'dropdown',
          filterId: 'productType',
          filterDropdownProps: {
            selectValue: getFilterSelectedValue('productType', 'chargerCertificates', false),
            selectOptions: chargerProductTypeReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'productType', false)
            },
            selectDropDownId: 'charger-charger-type-filter-dropdown',
            isMultiSelect: true
          }
        },
        {
          filterLabel: 'Chargers',
          filterType: 'asyncDropdown',
          filterId: 'chargerId',
          asyncDropdownProps: {
            fetchOptions: (search: string, offset: number, limit: number) =>
                dispatch(fetchChargerVisibleIdsPaginatedList({ search, offset, limit })),
            selected: getFilterSelectedValue('chargerId', 'chargerCertificates', false),
            onSelectedChange: (val): void => {
                handleFilterDataChange(val, 'chargerId', false)
            },
            selectDropDownId: 'alarm-chargerId-filter-dropdown',
            isMultiSelect: true
          }
        },
        {
          filterLabel: 'Certificate Status',
          filterType: 'dropdown',
          filterId: 'chargerCertificateStatus',
          filterDropdownProps: {
            selectValue: getFilterSelectedValue('chargerCertificateStatus', 'chargerCertificates', false),
            selectOptions: certificateStatusReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'chargerCertificateStatus', false)
            },
            selectDropDownId: 'charger-charger-type-filter-dropdown',
            isMultiSelect: true
          }
        },
        {
          filterLabel: 'Generated By',
          filterId: 'certificateGeneretedBy',
          filterType: 'dropdown',
          filterDropdownProps: {
            selectValue: getFilterSelectedValue('certificateGeneretedBy', 'chargerCertificates', false),
            selectOptions: userReferenceData ?? [],
            handleSelectOptionChange: (val) : void => {
                handleFilterDataChange(val,'certificateGeneretedBy',false);
            },
            selectDropDownId: 'charger-certificate-generated-by-filter-dropdown',
            isMultiSelect: true
          } 
        },
        {
          filterLabel: 'Generated On',
          filterType: 'dateFilter',
          filterId: 'certificateCreatedAtInterval',
          dateFilterProps: {
            datePickerId: 'alarm__time__stamp__set__at__date__picker',
            datePickerClassName: '',
            selectsRange: true,
            startDate: filterCertificateCreatedAtTimeStampValue?.[0] ? new Date(filterCertificateCreatedAtTimeStampValue?.[0]) : null,
            endDate: filterCertificateCreatedAtTimeStampValue?.[1] ? new Date(filterCertificateCreatedAtTimeStampValue?.[1]) : null,
            onChange: (val): void => {
              handleFilterDataChange(val, 'certificateCreatedAtInterval', false)
            },
          }
        },
        {
          filterLabel: 'Expiry Date Range',
          filterType: 'dateFilter',
          filterId: 'certificateExpiryIntervalRange',
          dateFilterProps: {
            datePickerId: 'alarm__time__stamp__set__at__date__picker',
            datePickerClassName: '',
            selectsRange: true,
            startDate: filterCertificateExpiryTimeStampValue?.[0] ? new Date(filterCertificateExpiryTimeStampValue?.[0]) : null,
            endDate: filterCertificateExpiryTimeStampValue?.[1] ? new Date(filterCertificateExpiryTimeStampValue?.[1]) : null,
            onChange: (val): void => {
              handleFilterDataChange(val, 'certificateExpiryIntervalRange', false)
            },
          }
        }

      ]
    return chargerFilters
  }, [  chargerProductTypeReferenceData, chargingStationReferenceData,screenFilters, chargerVisibleIdReferenceData, userReferenceData, chargerCertificatesInfoListTablePropsRef?.current])


  return (
    <>
      <TableDashboard
        tableId="manage__charger_certificates__table"
        tableClassName="manage__charger_certificates__table"
        header={'Charger Certificates'}
        searchBoxIncluded={true}
        searchBoxConfiguration={searchBoxConfiguration}
        gridColumns={chargerCertificatesInfoColumns}
        tableRows={getTableRows()}
        mobileViewConfiguration={mobileViewConfiguration}
        layouts={layouts}
        layoutView={'list'}
        headerActionBtns={headerActionBtns}
        listLayoutCallBack={layoutCallback}
        totalCount={chargerCertificatesInfoCount ??0}
        showSkeleton={!chargerCertificatesInfo}
        totalCountText={'certificates'}
        showLoader={loader}
        customNoDataComponent={NoDataComponentUsbCertificates}
        pageNumber={chargerCertificatesInfoListTableProps?.pageNumber}
        tablePageSize={chargerCertificatesInfoListTableProps?.pageSize}
        sortField={chargerCertificatesInfoListTableProps?.sortBy}
        sortOrder={chargerCertificatesInfoListTableProps?.order}
        referenceDataCallbacksFilters={['']}
        columnPrefrences={screenTableColumnPrefrences?.find((item) => item.screen === 'chargerCertificatesInfo')}
        handleColumnPrefrenceChange={handleColumnPrefrenceChange}
      />   

            {filtersVisible && <SidePopover
        id={'manage__chargers__side__filters'}
        open={filtersVisible}
        containerClassName='manage__chargers__side__filters'
        handleClose={handleFiltersClosePopOver}
        headerText={' Add Filters'}
        // eslint-disable-next-line react/no-children-prop
        children={<FilterLayout requiredReferenceDataCallbacks={['chargerVisibleId','productType','chargingStationId','chargerCertificateStatus','user']/* ['location', 'status', 'softwareVersion', 'severity', 'chargerVisibleId'] */}
          filters={chargerFilterProps} handleFilterDataChange={handleFilterDataChange} />}
        btnsList={filterFooterBtns}
      />
      } 
      <DashboardLoader showLoader={isPushingCertificate} loaderText='Pushing certificate...' />
    </>
  );
})

export default ChargerCertificatesInfo;
